import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import posthog from 'posthog-js'; // Import PostHog

// Get the API base URL from environment variables
export const API_BASE_URL = import.meta.env.VITE_API_URL;

let isRefreshing = false;
let failedQueue = [];

// Display warning if API URL is not defined
if (!API_BASE_URL) {
  console.warn('API URL not defined in environment variables. Using fallback URL.');
}

// Create a pre-configured axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
  },
});

// Add a request interceptor to include the Authorization token
apiClient.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      config.headers.Authorization = `${accessToken}`;
    }
    // Capture API request initiated, if PostHog is loaded
    if (posthog.__loaded && config.url) {
      posthog.capture('api_request_initiated', {
        method: config.method?.toUpperCase(),
        endpoint: config.url,
        baseURL: config.baseURL,
        params: config.params,
      });
    }
    return config;
  },
  (error) => {
    // Capture API request error, if PostHog is loaded
    if (posthog.__loaded && error.config && error.config.url) {
      posthog.capture('api_request_failed_before_send', {
        method: error.config.method?.toUpperCase(),
        endpoint: error.config.url,
        baseURL: error.config.baseURL,
        error_message: error.message,
      });
    }
    return Promise.reject(error);
  }
);

// Add a response interceptor to capture API responses and errors
apiClient.interceptors.response.use(
  (response) => {
    // Capture successful API responses, if PostHog is loaded
    if (posthog.__loaded && response.config?.url) {
      posthog.capture('api_request_successful', {
        method: response.config.method?.toUpperCase(),
        endpoint: response.config.url,
        baseURL: response.config.baseURL,
        status: response.status,
      });
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Log the initial failure
    if (posthog.__loaded && originalRequest?.url) {
      posthog.capture('api_request_failed', {
        method: error.config.method?.toUpperCase(),
        endpoint: error.config.url,
        baseURL: error.config.baseURL,
        status: error.response?.status,
        error_message: error.message,
        is_401_error: error.response?.status === 401,
      });
    }

    // If the error is a 401 and it's not a request to the refresh token endpoint
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If a refresh is already in progress, queue the failed request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['Authorization'] = token;
            return apiClient(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        // No refresh token, logout user
        window.location.href = '/admin/signin';
        return Promise.reject(error);
      }

      try {
        const refreshResponse = await apiService.auth.refreshToken(refreshToken);
        const { accessToken, idToken } = refreshResponse.data;

        // Update stored tokens
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('idToken', idToken);

        // Update the authorization header for new requests
        apiClient.defaults.headers.common['Authorization'] = accessToken;
        originalRequest.headers['Authorization'] = accessToken;

        // Retry all queued requests with the new token
        failedQueue.forEach(prom => prom.resolve(accessToken));
        failedQueue = [];

        // Retry the original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        // If refresh fails, clear tokens and redirect to login
        failedQueue.forEach(prom => prom.reject(refreshError));
        failedQueue = [];
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('idToken');
        posthog.reset();
        window.location.href = '/admin/signin'; // Force redirect
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Authentication-related handling
interface UserSignupDTO {
  firstName?: string;
  lastName?: string;
  countryCode: string;
  phoneNumber: string;
  group?: string;
  otp?: string;
  otpIdentifier?: string;
  userIdentifier?: string; // Cognito username, used in login/complete
  profilePicture?: string; // Base64 encoded image data
}

interface InitiateResponseData {
  otpIdentifier: string;
  userIdentifier?: string; // Optional, as it's not present in signup initiate
}

interface InitiateResponse {
  data: InitiateResponseData;
  message: string | null;
}

interface CompleteSignupResponse {
  otpIdentifier: null;
  message: string;
}

interface AuthTokens {
  accessToken: string;
  expiresIn: number;
  tokenType: string;
  refreshToken: string;
  idToken: string;
}

interface CompleteLoginResponse {
  data: AuthTokens;
  message: string;
}

interface RefreshTokenResponse {
  accessToken: string;
  expiresIn: number;
  tokenType: string;
  idToken: string;
}

// Helper to decode IdToken
export const decodeIdToken = (idToken: string): any | null => {
  try {
    return jwtDecode(idToken);
  } catch (error) {
    console.error("Failed to decode ID token:", error);
    if (posthog.__loaded) {
      posthog.capture('id_token_decode_failed', {
        error_message: (error as Error).message,
        token_snippet: idToken.substring(0, 20) // Log a snippet for debugging, not the whole token
      });
    }
    return null;
  }
};

// Common API endpoints
export const apiService = {
  // Authentication endpoints
  auth: {
    initiateSignup: (userData: UserSignupDTO): Promise<{ data: InitiateResponse }> =>
      apiClient.post('/auth/signup/initiate', userData),
    completeSignup: (userData: UserSignupDTO): Promise<{ data: CompleteSignupResponse }> =>
      apiClient.post('/auth/signup/complete', userData),
    initiateLogin: (userData: Pick<UserSignupDTO, 'countryCode' | 'phoneNumber'>): Promise<{ data: InitiateResponse }> =>
      apiClient.post('/auth/login/initiate', userData),
    completeLogin: (userData: UserSignupDTO): Promise<{ data: CompleteLoginResponse }> =>
      apiClient.post('/auth/login/complete', userData),
    refreshToken: (refreshToken: string): Promise<{ data: RefreshTokenResponse }> =>
      apiClient.post('/auth/refresh', refreshToken, { headers: { 'Content-Type': 'text/plain' } }),
  },

  // User profile operations
  users: {
    getProfile: () =>
      apiClient.get('/auth/user/profile'),
    updateProfilePicture: (profilePictureData: string) =>
      apiClient.put('/auth/user/profile-picture', { profilePicture: profilePictureData }),
    getUserSummary: (userId: string) =>
      apiClient.get(`/auth/user/${userId}/summary`),
  },

  // User management endpoints (Admin only)
  userManagement: {
    listUsers: (params?: { paginationToken?: string; limit?: number; filterByGroup?: string }) => {
      const queryParams = new URLSearchParams();
      if (params?.paginationToken) queryParams.append('paginationToken', params.paginationToken);
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.filterByGroup) queryParams.append('filterByGroup', params.filterByGroup);
      const queryString = queryParams.toString();
      return apiClient.get(`/users${queryString ? '?' + queryString : ''}`);
    },
    getUserDetails: (userId: string) =>
      apiClient.get(`/users/${userId}`),
    updateUser: (userId: string, updateData: { firstName?: string; lastName?: string; email?: string; phoneNumber?: string }) =>
      apiClient.put(`/users/${userId}`, updateData),
    updateUserStatus: (userId: string, enabled: boolean) =>
      apiClient.put(`/users/${userId}/status`, { enabled }),
    addUserToGroup: (userId: string, groupName: string) =>
      apiClient.post(`/users/${userId}/groups`, { groupName }),
    removeUserFromGroup: (userId: string, groupName: string) =>
      apiClient.delete(`/users/${userId}/groups/${groupName}`),
    deleteUser: (userId: string) =>
      apiClient.delete(`/users/${userId}`),
  },

  // Location and geocoding endpoints
  location: {
    getAddress: (latitude: number, longitude: number) =>
      apiClient.get(`/location/address?latitude=${latitude}&longitude=${longitude}`),
  },

  // Vendor-related endpoints
  vendors: {
    // Modified to support pagination and search
    list: (queryParams = '') =>
      apiClient.get(`/vendors${queryParams ? '?' + queryParams : ''}`),
    check: (vendorId: string) =>
      apiClient.get(`/vendors/check?vendorId=${vendorId}`),
    get: (vendorId: string) =>
      apiClient.get(`/vendors/${vendorId}`),
    create: (vendorId: string, vendorData: any) =>
      apiClient.post(`/vendors/${vendorId}`, vendorData),
    update: (vendorId: string, vendorData: any) =>
      apiClient.put(`/vendors/${vendorId}`, vendorData),
    delete: (vendorId: string) =>
      apiClient.delete(`/vendors/${vendorId}`),
    // Initiate vendor verification (send OTP)
    initiateVerification: (vendorId: string, verificationData: {
      firstName: string;
      lastName: string;
      countryCode: string;
      phone: string;
    }) =>
      apiClient.post(`/vendors/${vendorId}/verification/initiate`, verificationData),

    initiateConfirmation: (vendorId: string) =>
      apiClient.post(`/vendors/${vendorId}/initiate-confirmation`),
    completeConfirmation: (vendorId: string, otpData: { otp: string; otpIdentifier: string }) =>
      apiClient.post(`/vendors/${vendorId}/complete-confirmation`, otpData),
    verifyOtp: (vendorId: string, otpData: { // This seems related to the self-service flow, leaving as is.
      otp: string;
      otpIdentifier: string;
    }) =>
      apiClient.post(`/vendors/${vendorId}/verification/complete`, otpData),

    // Resend OTP
    resendOtp: (vendorId: string, phone: string) =>
      apiClient.post(`/vendors/${vendorId}/verification/resend`, { phone }),
  },

  // QR code-related endpoints
  qrcodes: {
    generate: (categoryId?: string) =>
      apiClient.post('/qrcodes/generate', categoryId ? { categoryId } : {}),
    check: (qrId: string) =>
      apiClient.get(`/qrcodes/${qrId}/check`),
    associate: (qrId: string, vendorId: string) =>
      apiClient.post('/qrcodes/associate', { qrId, vendorId }),
    list: (vendorId: string) =>
      apiClient.get(`/vendors/${vendorId}/qrcodes`),
    // New subscription-aware endpoints
    checkAllowed: (qrId: string) =>
      apiClient.get(`/qrcodes/${qrId}/allowed-info`),
    getAvailableSubscriptions: (vendorId: string, categoryId: string) =>
      apiClient.get(`/qrcodes/vendors/${vendorId}/subscriptions/category/${categoryId}`),
    getAllSubscriptionsWithStatus: (vendorId: string, categoryId: string) =>
      apiClient.get(`/qrcodes/vendors/${vendorId}/subscriptions/category/${categoryId}/all`),
    associateWithSubscription: (qrId: string, vendorId: string, subscriptionId: string) =>
      apiClient.post('/qrcodes/associate-with-subscription', { qrId, vendorId, subscriptionId }),
    // Dashboard endpoint (Admin only)
    getDashboard: (category?: string, status?: string) => {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (status) params.append('status', status);
      const queryString = params.toString();
      return apiClient.get(`/qrcodes/dashboard${queryString ? '?' + queryString : ''}`);
    },
  },

  // Vendor order-related endpoints
  vendorOrders: {
    getActivePricing: () =>
      apiClient.get('/vendor-orders/pricing'),
    getSubscriptionTiers: () =>
      apiClient.get('/vendor-orders/subscription-tiers'),
    getPricingRules: (pricingId: string) =>
      apiClient.get(`/vendor-orders/pricing/${pricingId}/rules`),
    create: (orderData: any) =>
      apiClient.post('/vendor-orders/create', orderData),
    verify: (orderId: string) =>
      apiClient.post(`/vendor-orders/${orderId}/verify`),
  },
  // Sales and analytics endpoints
  sales: {
    // Admin-only endpoints
    getAllAnalytics: () =>
      apiClient.get('/sales/analytics'),
    getAnalyticsByDateRange: (startDate: string, endDate: string) =>
      apiClient.get(`/sales/analytics/date-range?startDate=${startDate}&endDate=${endDate}`),
    getExpiringVendors: (endDate: string) =>
      apiClient.get(`/sales/expiring-vendors?endDate=${endDate}`),
    
    // Operations endpoints
    getMySalesData: (startDate?: string, endDate?: string) => {
      let url = '/sales/operations/my-sales';
      const params = [];
      if (startDate) params.push(`startDate=${startDate}`);
      if (endDate) params.push(`endDate=${endDate}`);
      if (params.length > 0) url += '?' + params.join('&');
      return apiClient.get(url);
    },
    
    // Vendor/Admin/Operations endpoints
    getVendorSubscriptions: (vendorId?: string) => {
      let url = '/sales/vendor-subscriptions';
      if (vendorId) url += `?vendorId=${vendorId}`;
      return apiClient.get(url);
    },
    
    // Personal subscriptions for all roles (phone-based ownership)
    getMySubscriptions: () =>
      apiClient.get('/sales/my-subscriptions'),
    
    // Order details with CashFree webhook data
    getOrderDetails: (orderId: string) =>
      apiClient.get(`/sales/order-details/${orderId}`),
  },

  // Upload-related endpoints
  uploads: {
    // Get presigned URLs for S3 uploads
    getPresignedUrls: (vendorId: string, count: number, fileTypes: string[]) =>
      apiClient.post('/s3/presigned-urls', {
        vendorId,
        count,
        fileTypes,
        version: 1, // Default version, can be parametrized if needed
      }),
  },

  // Menu-related endpoints
  menus: {
    getById: (menuId: string) =>
      apiClient.get(`/menus/${menuId}`),
    getByVendor: (vendorId: string) => // Might be deprecated if getLatestMenu is primary
      apiClient.get(`/menus/vendor/${vendorId}`),
    getLatestMenu: (vendorId: string) =>
      apiClient.get(`/menus/vendor/${vendorId}/latest`),
    updateMenu: (menuId: string, menuData: any) =>
      apiClient.put(`/menus/${menuId}`, menuData),
    createMenu: (vendorId: string, menuData: any) =>
      apiClient.post(`/menus/vendor/${vendorId}`, menuData),
    addCategory: (menuId: string, categoryData: any) =>
      apiClient.post(`/menus/${menuId}/categories`, categoryData),
    updateCategory: (menuId: string, categoryId: string, categoryData: any) =>
      apiClient.put(`/menus/${menuId}/categories/${categoryId}`, categoryData),
    deleteCategory: (menuId: string, categoryId: string) =>
      apiClient.delete(`/menus/${menuId}/categories/${categoryId}`),
    addItem: (menuId: string, categoryId: string, itemData: any) =>
      apiClient.post(`/menus/${menuId}/categories/${categoryId}/items`, itemData),
    updateItem: (menuId: string, categoryId: string, itemId: string, itemData: any) =>
      apiClient.put(`/menus/${menuId}/categories/${categoryId}/items/${itemId}`, itemData),
    deleteItem: (menuId: string, categoryId: string, itemId: string) =>
      apiClient.delete(`/menus/${menuId}/categories/${categoryId}/items/${itemId}`),
    updateItemAvailability: (menuId: string, categoryId: string, itemId: string, available: boolean) =>
      apiClient.patch(`/menus/${menuId}/categories/${categoryId}/items/${itemId}/availability?available=${available}`),
    updateBulkItemAvailability: (menuId: string, categoryId: string, bulkUpdateData: any) =>
      apiClient.patch(`/menus/${menuId}/categories/${categoryId}/items/availability`, bulkUpdateData),
  },

  // Pricing-related endpoints (Admin only)
  pricing: {
    getAll: () =>
      apiClient.get('/pricing'),
    getActive: () =>
      apiClient.get('/pricing/active'),
    getSubscriptionTiers: () =>
      apiClient.get('/pricing/subscription-tiers'),
    getBundled: () =>
      apiClient.get('/pricing/bundled'),
    getByCurrency: (currency: string) =>
      apiClient.get(`/pricing/currency/${currency}`),
    getById: (pricingId: string) =>
      apiClient.get(`/pricing/${pricingId}`),
    getDefault: (currency: string) =>
      apiClient.get(`/pricing/default/${currency}`),
    create: (pricingData: any) =>
      apiClient.post('/pricing', pricingData),
    update: (pricingId: string, pricingData: any) =>
      apiClient.put(`/pricing/${pricingId}`, pricingData),
    delete: (pricingId: string) =>
      apiClient.delete(`/pricing/${pricingId}`),
    setAsDefault: (pricingId: string) =>
      apiClient.patch(`/pricing/${pricingId}/default`),
    getCurrencies: () =>
      apiClient.get('/pricing/currencies'),
  },

  // Product Category-related endpoints (Admin and Operations)
  productCategories: {
    getAll: () =>
      apiClient.get('/product-categories'),
    getActive: () =>
      apiClient.get('/product-categories/active'),
    getById: (categoryId: string) =>
      apiClient.get(`/product-categories/${categoryId}`),
    getActiveShortNames: () =>
      apiClient.get('/product-categories/short-names/active'),
    create: (categoryData: any) =>
      apiClient.post('/product-categories', categoryData),
    update: (categoryId: string, categoryData: any) =>
      apiClient.put(`/product-categories/${categoryId}`, categoryData),
    delete: (categoryId: string) =>
      apiClient.delete(`/product-categories/${categoryId}`),
    validate: (shortName: string) =>
      apiClient.get(`/product-categories/validate/${shortName}`),
  },

  // Analytics-related endpoints (Admin only)
  analytics: {
    getDashboard: (params?: Record<string, string>) =>
      apiClient.get('/analytics/dashboard', { params }),
    getVendorCreation: (params?: Record<string, string>) =>
      apiClient.get('/analytics/vendor-creation', { params }),
    getOperationPerson: (operationPersonId: string, params?: Record<string, string>) =>
      apiClient.get(`/analytics/operation-person/${operationPersonId}`, { params }),
    getPostHog: (params?: Record<string, string>) =>
      apiClient.get('/analytics/posthog', { params }),
    getQRScans: (params?: Record<string, string>) =>
      apiClient.get('/analytics/qr-scans', { params }),
    getTopVendors: (params?: Record<string, string>) =>
      apiClient.get('/analytics/top-vendors', { params }),
    getDeletedVendors: (params?: Record<string, string>) =>
      apiClient.get('/analytics/deleted-vendors', { params }),
  },

  // Dashboard-related endpoints
  dashboard: {
    getMetrics: (period: string = 'month') =>
      apiClient.get(`/dashboard/metrics?period=${period}`),
    getChartData: (chartType: string, period: string = 'month', startDate?: string, endDate?: string) => {
      let url = `/dashboard/charts/${chartType}?period=${period}`;
      if (startDate) url += `&startDate=${startDate}`;
      if (endDate) url += `&endDate=${endDate}`;
      return apiClient.get(url);
    },
    getRecentActivity: (limit: number = 10) =>
      apiClient.get(`/dashboard/activity?limit=${limit}`),
  },
};